/* eslint-disable tss-unused-classes/unused-classes -- classes are used via returned object from makeStyles hook */
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';
import { BALANCE_SHEET_COLUMN_GAP_WIDTH, getBalanceSheetPadding } from '../../BalanceSheetTable.layout';
import { BalanceSheetCellPosition, BalanceSheetDensity } from '../../BalanceSheetTable.types';

/**
 * Presentation context colors (neutral grayscale for white-labeling)
 * Note: summaryBackground uses NEUTRAL_GRAY[100] to match BalanceSheetCell summary backgrounds
 */
const PRESENTATION_COLORS = {
  textPrimary: COLORS.NEUTRAL_GRAY[900],
  textSecondary: COLORS.NEUTRAL_GRAY[500],
  border: COLORS.NEUTRAL_GRAY[700],
  summaryBackground: COLORS.NEUTRAL_GRAY[100]
};

/**
 * App context colors (navy theme)
 */
const APP_COLORS = {
  textPrimary: COLORS.TEXT.PRIMARY,
  textSecondary: COLORS.TEXT.SECONDARY,
  border: COLORS.GRAY[500],
  summaryBackground: COLORS.NAVY[50]
};
export interface BalanceSheetHeaderStyleProps {
  cellPosition: BalanceSheetCellPosition;
  density: BalanceSheetDensity;
  alignment: 'left' | 'right';
  isPresentation: boolean;
  indentStartContent: boolean;
  minWidth?: number;
  maxWidth?: number;
}
export const useBalanceSheetHeaderStyles = makeStyles<BalanceSheetHeaderStyleProps>()((_theme: Theme, props) => {
  const {
    cellPosition,
    density,
    alignment,
    isPresentation,
    indentStartContent
  } = props;
  const colors = isPresentation ? PRESENTATION_COLORS : APP_COLORS;
  // In app mode, start headers need 24px extra left padding to align with
  // category text that sits after the expand icon (16px icon + 8px gap).
  const indentLeft = cellPosition === 'start' && !isPresentation && indentStartContent ? 24 : 0;
  const padding = getBalanceSheetPadding(density, cellPosition, {
    indentLeft
  });
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: alignment === 'right' ? 'flex-end' : 'flex-start',
      // Grid sizes this header via the column track; minWidth: 0 allows
      // content to shrink below its intrinsic size so text truncates.
      minWidth: 0,
      borderBottom: `2px solid ${colors.border}`,
      backgroundColor: cellPosition === 'summary' ? colors.summaryBackground : 'transparent',
      ...padding
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      width: '100%',
      minWidth: 0,
      alignItems: alignment === 'right' ? 'flex-end' : 'flex-start'
    },
    primaryContent: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      minWidth: 0,
      justifyContent: alignment === 'right' ? 'flex-end' : 'flex-start'
    },
    text: {
      width: '100%',
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: FONT_WEIGHTS.bold,
      color: colors.textPrimary,
      textAlign: alignment,
      // Only truncate text in start position (asset column header); data column headers can wrap
      ...(cellPosition === 'start' ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0
      } : {
        // Allow text to wrap for data column headers (e.g., "Rogers Revocable Trust")
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        overflowWrap: 'anywhere',
        minWidth: 0
      })
    },
    subtext: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: FONT_WEIGHTS.regular,
      color: colors.textSecondary,
      textAlign: alignment,
      // Only truncate text in start position; data column headers should not truncate
      ...(cellPosition === 'start' ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      } : {
        whiteSpace: 'nowrap'
      })
    },
    columnGap: {
      width: BALANCE_SHEET_COLUMN_GAP_WIDTH,
      flexShrink: 0,
      alignSelf: 'stretch'
    }
  };
});